/**
 * Readiness helpers for the Mapbox browser libraries.
 *
 * The Mapbox scripts are included from util/includeScripts.js with the `async`
 * attribute, so they are not guaranteed to have executed by the time React
 * hydrates. Anything that touches window.mapboxgl or window.mapboxSdk therefore
 * has to wait for them instead of assuming they are already there.
 *
 * Polling is used rather than a 'load' listener on the script tag, because the
 * tag can enter the page through several routes: rendered into <head> by SSR,
 * injected client-side by React Helmet, or served from the browser cache (in
 * which case it may already have executed before any listener is attached).
 * Polling covers all of them without needing to know which one happened.
 */

const POLL_INTERVAL = 50;
const LOAD_TIMEOUT = 20000;

// The access token is handed over by IncludeScripts as soon as the config is
// known. It is kept here so it can be applied the moment mapboxgl appears,
// rather than depending on a React re-render happening at the right time.
let pendingAccessToken = null;
let readyPromise = null;

export const isMapboxLoaded = () =>
  typeof window !== 'undefined' && !!window.mapboxgl && !!window.mapboxSdk;

const MAPBOX_CSS_ID = 'mapbox_GL_CSS_link';
const MAPBOX_CANARY_STYLE_ID = 'mapbox_GL_canary';
const MAPBOX_CSS_HREF = 'https://api.mapbox.com/mapbox-gl-js/v1.0.0/mapbox-gl.css';

/**
 * Inject the Mapbox GL stylesheet on demand (client-side only), exactly once.
 *
 * Previously this stylesheet was rendered into <head> globally (via
 * util/includeScripts.js), so it was a render-blocking request on EVERY page —
 * including pages with no map (e.g. the landing page), delaying FCP/LCP.
 *
 * Mapbox GL's CSS only styles the interactive WebGL map (controls, markers,
 * popups, attribution). It is not needed for the address autocomplete/geocoder
 * (which uses its own styles) nor for the static map (a plain <img>). So it is
 * now requested only when an interactive map component actually mounts, which
 * removes the render-blocking request from every non-map page with no visual or
 * functional change to the map itself.
 */
export const ensureMapboxCss = () => {
  if (typeof document === 'undefined') {
    return; // SSR: nothing to do; the map only renders client-side.
  }
  if (document.getElementById(MAPBOX_CSS_ID)) {
    return; // Already injected (e.g. a second map on the same page).
  }

  // Mapbox GL runs a synchronous "canary" check when a Map is constructed: it looks for a
  // stylesheet rule painting `.mapboxgl-canary` salmon (rgb(250,128,114)) and logs a
  // "missing CSS declarations for Mapbox GL JS" console warning if the rule isn't present yet.
  // The real stylesheet (<link> below) is fetched asynchronously, so that check often runs
  // before it has loaded — producing a spurious warning even though the CSS arrives a moment
  // later. Injecting just the canary rule inline (synchronous, applied immediately) makes the
  // check pass every time; the <link> still supplies the full map styling. The color matches
  // both the CDN v1 stylesheet used at runtime and the npm v3 stylesheet.
  const canary = document.createElement('style');
  canary.id = MAPBOX_CANARY_STYLE_ID;
  canary.textContent = '.mapboxgl-canary { background-color: salmon; }';
  document.head.appendChild(canary);

  const link = document.createElement('link');
  link.id = MAPBOX_CSS_ID;
  link.rel = 'stylesheet';
  link.href = MAPBOX_CSS_HREF;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
};

const applyAccessToken = () => {
  const canApply =
    typeof window !== 'undefined' &&
    window.mapboxgl &&
    pendingAccessToken &&
    !window.mapboxgl.accessToken;

  if (canApply) {
    window.mapboxgl.accessToken = pendingAccessToken;
  }
};

/**
 * Store the access token and apply it if mapboxgl is already available.
 * Safe to call repeatedly and during SSR.
 */
export const setMapboxAccessToken = token => {
  if (token) {
    pendingAccessToken = token;
    applyAccessToken();
  }
};

/**
 * Resolves once both Mapbox libraries are available and the access token has
 * been applied. Rejects if they don't show up within LOAD_TIMEOUT.
 *
 * @return {Promise<void>}
 */
export const whenMapboxLoaded = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Mapbox libraries are only available in the browser'));
  }
  if (isMapboxLoaded()) {
    applyAccessToken();
    return Promise.resolve();
  }
  if (readyPromise) {
    return readyPromise;
  }

  readyPromise = new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (isMapboxLoaded()) {
        window.clearInterval(intervalId);
        applyAccessToken();
        resolve();
      } else if (Date.now() - startedAt > LOAD_TIMEOUT) {
        window.clearInterval(intervalId);
        // Clear the memoized promise so a later attempt can start polling
        // again, e.g. once the user recovers from a flaky connection.
        readyPromise = null;
        reject(new Error('Mapbox libraries are required for GeocoderMapbox'));
      }
    }, POLL_INTERVAL);
  });

  return readyPromise;
};
